import React, { FC, useEffect, useState } from 'react';
import { COMMAND_STATE } from '../../constants/command-status';
import { AccountSubCmds } from '../../constants/sub-commands';
import Commands from '../../constants/commands';
import { useSelector } from 'react-redux';
import { IAppState } from '../../store/types';
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, useFormikContext } from 'formik';
import { useLocale } from '../../contexts/localeContext';
import { useDialog } from '../dialog/DialogContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { PasswordConfig } from "../../models/SecurityPolicyPasswordConfig";
import { SecurityPolicyPasswordConfigHelper } from "../../utilities/securityPolicyPasswordConfigHelper";
import { requiredValidation, validateConfirmPassword, validatePassword, validateSecurityPolicyPassword } from '../../utilities/Validation';
import PasswordPolicyHint from '../change-password/PasswordPolicyHint';
import { ExecuteCommand } from '../../models/ExecuteCommand';
import { executeCommand } from '../../services/executeCommandService';
import Label from '../label/label';
import { toast } from "react-toastify";
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { isAdminOrAbove } from '../../constants/preset-constants';
import { isSysdbaAccount } from '../../constants/logins-account-types';

interface Props {
    itemId: string,
    isLdapLoginType: boolean
    isAuthLoginType: boolean
    isAuthWithGroupsLoginType: boolean
}

const PasswordDetails: FC<Props> = ({ itemId, isLdapLoginType, isAuthLoginType, isAuthWithGroupsLoginType }) => {
    const user = useSelector((state: IAppState) => (state.userInfo));
    const { errors, values } = useFormikContext<any>();
    const [busy, setBusy] = useState(false);
    const [showPwdForm, setShowPwdForm] = useState(false)
    const [showOldPassword, setShowOldPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const { translate } = useLocale();
    const dialog = useDialog();
    const [passwordConfig, setPasswordConfig] = useState<PasswordConfig>({
        enforceUppercase: false,
        enforceLowercase: false,
        enforceDigits: false,
        enforceSpecialCharacters: false,
        enforcePasswordMinLength: false,
        passwordMinLength: -1,
    });

    useEffect(() => {
        fetchAssignedUserSecurityPolicyPasswordConfig();
    }, []);

    const fetchAssignedUserSecurityPolicyPasswordConfig = async () => {
        try {
            const assignedUserSecurityPolicyPasswordConfig = await SecurityPolicyPasswordConfigHelper.fetchAssignedUserSecurityPolicyPasswordConfig(values.securityPolicyId, translate);
            if (assignedUserSecurityPolicyPasswordConfig) {
                setPasswordConfig(assignedUserSecurityPolicyPasswordConfig);
            }
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    };

    /**
     * Allow administrators & system owners to reset MFA for another user.
     * Reset MFA means clearing the secret so that the next login attempt shows a QR Code for registering MFA with the authenticator app.
     * @param event
     */
    const onResetMfa = (event) => {
        try {
            setBusy(true);

            const command: ExecuteCommand = {
                cmd: Commands.Account.label,
                cmdSubType: AccountSubCmds.Reset_Mfa,
                entityId: `` + itemId,
                jsonResponse: ""
            }

            executeCommand(command).then((response: any) => {
                if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {
                    toast.success(translate.byKey(TranslationKeys.resetMfaSuccess));
                }
                else {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.resetMfaFailed) });
                }
            }).catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            })
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    };

    /**
     * Check if changing this operator's password is allowed.
     * We don't store passwords for LDAP accounts so they can't change them here.
     * SYSDBA account is protected.
     * @returns {boolean} True if changing passwords is supported for this account, false otherwise.
     */
    const canChangePassword = () => {
        return !isLdapLoginType && !isAuthLoginType && !isAuthWithGroupsLoginType && !isSysdbaAccount(parseInt(itemId));
    }

    return (
        <>

            {isAdminOrAbove(user.profileId) &&
                <div className="text-md font-bold mb-3">{translate.byKey(TranslationKeys.resetMfa)}</div>
            }
            {isAdminOrAbove(user.profileId) &&
                <button
                    id='resetMfaButton'
                    onClick={(event) => onResetMfa(event)}
                    className="border-portal-blue-primary border-solid border-2 text-portal-blue-primary py-1 px-2 rounded-full text-xs"
                    type="button" >
                    <span className="text-sm">{translate.byKey(TranslationKeys.reset)}</span>
                </button>
            }

            {canChangePassword() && <div className="text-md font-bold mt-6 mb-3">{translate.byKey(TranslationKeys.changePassword)}</div>}
            {!showPwdForm && canChangePassword() &&
                <button
                    id='changePasswordButton'
                    onClick={() => setShowPwdForm(true)}
                    className="border-portal-blue-primary border-solid border-2 text-portal-blue-primary py-1 px-2 rounded-full  text-xs"
                    type="button" >
                    <span className="text-sm">{translate.byKey(TranslationKeys.changePassword)}</span>
                </button>}
            {showPwdForm && canChangePassword() &&
                <>
                    <div className='flex flex-row'>
                        <div className="flex flex-col flex-1 pr-3 mt-2">
                            <Label
                                htmlFor='ulConfirmPassword'
                                className="text-xs text-gray-600 mt-1">
                                {translate.byKey(TranslationKeys.oldPassword)}
                            </Label>
                            <div className='flex flex-row justify-between'>
                                <Field
                                    id='oldPasswordInputField'
                                    validate={(value) => requiredValidation(value, translate)}
                                    name="ulConfirmPassword"
                                    type={showOldPassword ? 'text' : 'password'}
                                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full" />
                                <div className="flex relative w-6 ml-1">
                                    <FontAwesomeIcon
                                        className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                        onClick={() => setShowOldPassword(!showOldPassword)}
                                        icon={showOldPassword ? faEye : faEyeSlash} />
                                </div>
                            </div>
                            {errors.ulConfirmPassword ? <span id='oldPasswordLabelId' className='text-red-600 text-xs'>{errors.ulConfirmPassword}</span> : null}
                        </div>
                        <div className="flex flex-col flex-1 pr-3 mt-2">
                            {/* Placeholder */}
                        </div>
                    </div>
                    <div className='flex flex-row'>
                        <div className="flex flex-col flex-1 pr-3 mt-2">
                            <Label
                                htmlFor='ulPassword'
                                className="text-xs text-gray-600 mt-1">
                                {translate.byKey(TranslationKeys.newPassword)}
                                <PasswordPolicyHint passwordConfig={passwordConfig} />
                            </Label>
                            <div className='flex flex-row justify-between'>
                                <Field id='editPasswordFieldId'
                                    name="ulPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full"
                                    validate={(value) => validateSecurityPolicyPassword(value, passwordConfig, translate, true)} >
                                    {/*Needed for real-time validation as Formik only validates the previous value*/}
                                    {({ field, form }) => (
                                        <input
                                            {...field}
                                            type={showNewPassword ? 'text' : 'password'}
                                            className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                form.setFieldValue('ulPassword', newValue);
                                                form.setFieldError('ulPassword', validateSecurityPolicyPassword(newValue, passwordConfig, translate, true));
                                            }} />
                                    )}
                                </Field>
                                <div className="flex relative w-6 ml-1">
                                    <FontAwesomeIcon
                                        className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        icon={showNewPassword ? faEye : faEyeSlash} />
                                </div>
                            </div>
                            {errors.ulPassword ? <span id='editConfirmNewPasswordLabeld' className='text-red-600 text-xs'>{errors.ulPassword}</span> : null}
                        </div>
                        <div className="flex flex-col flex-1 pr-3 mt-2">
                            <Label htmlFor='confirmPassword' className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.confirmPassword)}:</Label>
                            <div className='flex flex-row justify-between'>
                                <Field
                                    id='editConfirmNewPasswordFieldId'
                                    validate={value => validateConfirmPassword(values.ulPassword, value, translate)}
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full" />
                                <div className="flex relative w-6 ml-1">
                                    <FontAwesomeIcon
                                        className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        icon={showConfirmPassword ? faEye : faEyeSlash} />
                                </div>
                            </div>
                            {errors.confirmPassword ? <span id='editConfirmNewPasswordLabeld' className='text-red-600 text-xs'>{errors.confirmPassword}</span> : null}
                        </div>
                    </div>
                </>
            }
        </>);
};

export default PasswordDetails;
